/**
 * Public hostnames for Welcome2Life. These are product domains, not secrets.
 * Do not put them in Firebase params or functions/.env — this file is the
 * only source of truth (functions, catalog, invite routing).
 *
 * For each app, hosts[0] is the live canonical hostname. Extra entries are
 * aliases kept during domain cutover so CORS / SSO keep working.
 *
 * Change hostnames here, then deploy functions + hosting.
 */

const HOME_HOSTS = Object.freeze([
  "welcome2life.org",
  "www.welcome2life.org",
  "home.welcome2life.org",
  "welcome2life.web.app",
  "welcome2life.firebaseapp.com",
]);

const ALLOWED_PARENT_DOMAINS = Object.freeze([
  "welcome2life.org",
  "productivity-cafe.com",
  "productivity-cafe.org",
]);

const LOCAL_DEV_ORIGINS = Object.freeze([
  "http://localhost:5000",
  "http://127.0.0.1:5000",
  "http://localhost:5002",
  "http://127.0.0.1:5002",
  "https://localhost",
]);

const APPS = Object.freeze({
  nodecast: Object.freeze({
    hosts: Object.freeze(["tv.welcome2life.org", "tv.productivity-cafe.com"]),
    ssoPathExact: "/api/auth/oidc/login",
  }),
  mealie: Object.freeze({
    hosts: Object.freeze(["recipe.welcome2life.org", "recipe.productivity-cafe.org"]),
    ssoPathPrefix: "/login",
  }),
  librechat: Object.freeze({
    hosts: Object.freeze(["ai.welcome2life.org", "ai10.productivity-cafe.org"]),
    ssoPathPrefix: "/login",
  }),
  watchparty: Object.freeze({
    hosts: Object.freeze(["watch.welcome2life.org", "watch.productivity-cafe.com"]),
  }),
  kasm: Object.freeze({
    hosts: Object.freeze(["remote.productivity-cafe.com"]),
  }),
});

const HOME_ORIGIN = `https://${HOME_HOSTS[0]}`;
const HOME_ORIGINS = Object.freeze(HOME_HOSTS.map((host) => `https://${host}`));
const HOSTING_CORS_ORIGINS = Object.freeze(HOME_ORIGINS.concat(LOCAL_DEV_ORIGINS));

/**
 * @param {string} host
 * @return {string}
 */
function httpsOrigin(host) {
  return `https://${host}`;
}

/**
 * Canonical public origin for an app (hosts[0]).
 * @param {string} appId
 * @return {string}
 */
function appCanonicalOrigin(appId) {
  const spec = APPS[appId];
  if (!spec || !spec.hosts || !spec.hosts.length) {
    return "";
  }
  return httpsOrigin(spec.hosts[0]);
}

/**
 * @param {string} hostname
 * @return {boolean}
 */
function isHomeHost(hostname) {
  return HOME_HOSTS.indexOf(String(hostname || "").toLowerCase()) !== -1;
}

/**
 * @param {string} hostname
 * @return {boolean}
 */
function isZepHost(hostname) {
  const host = String(hostname || "").toLowerCase();
  return host === "zep.us" || host.endsWith(".zep.us");
}

/**
 * Home aliases + any host under an owned parent domain.
 * @param {string} hostname
 * @return {boolean}
 */
function isOwnedHost(hostname) {
  const host = String(hostname || "").toLowerCase();
  if (!host) {
    return false;
  }
  if (isHomeHost(host)) {
    return true;
  }
  for (let i = 0; i < ALLOWED_PARENT_DOMAINS.length; i++) {
    const parent = ALLOWED_PARENT_DOMAINS[i];
    if (host === parent || host.endsWith(`.${parent}`)) {
      return true;
    }
  }
  return false;
}

/**
 * @param {string} origin
 * @return {boolean}
 */
function isLocalDevOrigin(origin) {
  return LOCAL_DEV_ORIGINS.indexOf(origin) !== -1;
}

/**
 * CORS allowlist: Zep, home aliases, *.welcome2life.org, *.productivity-cafe.*, local.
 * @param {string} origin
 * @return {boolean}
 */
function isAllowedCorsOrigin(origin) {
  if (!origin) {
    return false;
  }
  if (isLocalDevOrigin(origin)) {
    return true;
  }
  let parsed;
  try {
    parsed = new URL(origin);
  } catch (error) {
    return false;
  }
  if (parsed.protocol !== "https:") {
    return false;
  }
  return isZepHost(parsed.hostname) || isOwnedHost(parsed.hostname);
}

/**
 * @param {object} res
 * @param {string} origin
 */
function applyCors(res, origin) {
  if (isAllowedCorsOrigin(origin)) {
    res.set("Access-Control-Allow-Origin", origin);
    res.set("Vary", "Origin");
  }
}

/**
 * @param {string} hostname
 * @return {object|null}
 */
function findAppByHost(hostname) {
  const host = String(hostname || "").toLowerCase();
  const ids = Object.keys(APPS);
  for (let i = 0; i < ids.length; i++) {
    const spec = APPS[ids[i]];
    if (spec.hosts && spec.hosts.indexOf(host) !== -1) {
      return spec;
    }
  }
  return null;
}

/**
 * Post-/session OIDC target must be a known app login URL.
 * @param {string} nextUrl
 * @return {boolean}
 */
function isAllowedOidcLaunchTarget(nextUrl) {
  if (!nextUrl || !/^https:\/\//i.test(nextUrl)) {
    return false;
  }
  try {
    const parsed = new URL(nextUrl);
    const spec = findAppByHost(parsed.hostname);
    if (!spec) {
      return false;
    }
    if (spec.ssoPathExact) {
      return parsed.pathname === spec.ssoPathExact;
    }
    if (spec.ssoPathPrefix) {
      const prefix = spec.ssoPathPrefix;
      return parsed.pathname === prefix || parsed.pathname.startsWith(prefix);
    }
    return parsed.pathname === "/" || parsed.pathname === "";
  } catch (error) {
    return false;
  }
}

module.exports = {
  HOME_HOSTS,
  HOME_ORIGIN,
  HOME_ORIGINS,
  HOSTING_CORS_ORIGINS,
  ALLOWED_PARENT_DOMAINS,
  LOCAL_DEV_ORIGINS,
  APPS,
  appCanonicalOrigin,
  isHomeHost,
  isOwnedHost,
  isAllowedCorsOrigin,
  applyCors,
  isAllowedOidcLaunchTarget,
};
